
import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Image as ImageIcon, Film, Link2, FileText } from "lucide-react";

const typeIcon: Record<string, React.ReactNode> = {
  photo: <ImageIcon className="h-5 w-5" />,
  IMAGE: <ImageIcon className="h-5 w-5" />,
  video: <Film className="h-5 w-5" />,
  VIDEO: <Film className="h-5 w-5" />,
  link: <Link2 className="h-5 w-5" />,
  status: <FileText className="h-5 w-5" />,
  CAROUSEL_ALBUM: <ImageIcon className="h-5 w-5" />,
  REELS: <Film className="h-5 w-5" />,
  album: <ImageIcon className="h-5 w-5" />,
};

interface ImagePreviewProps {
  src: string | null | undefined;
  fallbackType?: string | null;
  size?: "sm" | "md";
}

export function ImagePreview({ src, fallbackType, size = "sm" }: ImagePreviewProps) {
  const [hovered, setHovered] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const ref = useRef<HTMLDivElement>(null);

  const baseSize = size === "md" ? "w-16 h-16 min-w-16" : "w-10 h-10 min-w-10";

  const handleEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({
        x: rect.right + 12,
        y: Math.min(rect.top, window.innerHeight - 400),
      });
    }
    setHovered(true);
  };

  if (!src || imgError) {
    return (
      <div className={`${baseSize} rounded-md bg-muted flex items-center justify-center text-muted-foreground flex-shrink-0`}>
        {typeIcon[fallbackType || ""] || <ImageIcon className="h-5 w-5" />}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className="relative flex-shrink-0"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        src={src}
        alt=""
        className={`${baseSize} rounded-md object-cover border border-border cursor-pointer`}
        onError={() => setImgError(true)}
      />
      {hovered && createPortal(
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{ left: pos.x, top: pos.y }}
        >
          <img
            src={src}
            alt=""
            className="max-w-80 max-h-96 rounded-lg object-contain border-2 border-[var(--primary)] shadow-2xl shadow-black/80 bg-black"
          />
        </div>,
        document.body
      )}
    </div>
  );
}
